import { AxiosError } from "axios";
import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import adminAxios from "@/lib/axios";

export interface IFeeType {
  id?: number;
  program_fee_id?: number;
  name: string;
  name_text_color?: string;
  category_color?: string;
  order_index: number;
  is_active?: boolean;
  status?: boolean;
}

export interface IFeeCategory {
  id?: number;
  program_fee_id?: number;
  name: string;
  name_text_color?: string;
  order_index: number;
  is_active?: boolean;
  status?: boolean;
}

export interface IFeeSemester {
  id?: number;
  program_fee_id?: number;
  name: string;
  semester_number?: number;
  order_index: number;
  is_active?: boolean;
  status?: boolean;
}

export interface IFeeValue {
  id?: number;
  fee_type_id: number;
  category_id: number;
  semester_id: number;
  amount?: number | null;
  amount_text_color?: string;
  is_active?: boolean;
  status?: boolean;
}

export interface IProgramFee {
  id?: number;
  page_id?: number;
  component_id?: number;
  title?: string;
  title_text_color?: string;
  note?: string;
  note_text_color?: string;
  other_fee?: string;
  status?: boolean;
  background_color?: string;
  table_header_background_color?: string;
  tabs?: IFeeType[];
  rows?: IFeeCategory[];
  columns?: IFeeSemester[];
  values?: IFeeValue[];
}

// Program Fee API methods
export async function createProgramFee(payload: Partial<IProgramFee>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateProgramFee(id: number, payload: Partial<IProgramFee>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getProgramFeeByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.GET_BY_COMPONENT_ID(componentId)
    );

    return response?.data;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getProgramFeeDataByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.GET_DATA_BY_COMPONENT_ID(componentId)
    );

    return response?.data;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// Fee Type (Tab) API methods
export async function createFeeType(payload: Partial<IFeeType>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.TYPE.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateFeeType(id: number, payload: Partial<IFeeType>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.TYPE.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteFeeType(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.TYPE.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getFeeTypesByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.TYPE.GET_BY_COMPONENT_ID(componentId)
    );

    return response?.data;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// Fee Category (Row) API methods
export async function createFeeCategory(payload: Partial<IFeeCategory>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.CATEGORY.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateFeeCategory(id: number, payload: Partial<IFeeCategory>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.CATEGORY.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteFeeCategory(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.CATEGORY.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getFeeCategoriesByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.CATEGORY.GET_BY_COMPONENT_ID(componentId)
    );

    return response?.data;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// Fee Semester (Column) API methods
export async function createFeeSemester(payload: Partial<IFeeSemester>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.SEMESTER.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateFeeSemester(id: number, payload: Partial<IFeeSemester>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.SEMESTER.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteFeeSemester(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.SEMESTER.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getFeeSemestersByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.SEMESTER.GET_BY_COMPONENT_ID(componentId)
    );

    return response?.data;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// Fee Value (Matrix Value) API methods
export async function createFeeValue(payload: Partial<IFeeValue>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.VALUE.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateFeeValue(id: number, payload: Partial<IFeeValue>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.VALUE.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteFeeValue(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.VALUE.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getFeeValuesByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.VALUE.GET_BY_COMPONENT_ID(componentId)
    );

    return response?.data;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function cloneProgramFeeWithNestedData(
  sourceProgramFeeId: number,
  targetPageComponentId: number,
  targetProgramFeeId?: number | null
) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE.CLONE,
      {
        source_program_fee_id: sourceProgramFeeId,
        target_page_component_id: targetPageComponentId,
        target_program_fee_id: targetProgramFeeId || null,
      }
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

